/**
 * Дашборд: состояние данных по всей сети.
 *
 * Сверху — полоса «Состояние сети»: заполненность, ход обработки и все
 * объекты плитками по дивизионам. Ниже две колонки: слева проблемные
 * записи, справа — замечания по категориям (они же фильтр таблицы),
 * объекты, с которых начать, и модели оборудования.
 *
 * Категория отбирает записи с этим замечанием, повторный клик снимает
 * отбор; снять его можно и крестиком у названия таблицы.
 */
import { useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { selectSwitches, selectGrouped, selectPrefixMap } from '../../store/slices/switchesSlice';
import { selectIsDeveloper, selectCanEdit, selectUser } from '../../store/slices/authSlice';
import { selectMagToDivision } from '../../store/slices/divisionsSlice';
import { setDashFilter } from '../../store/slices/uiSlice';
import {
  getIssueCounts, filterProblemRows, getProgressStats, getModelStats, getObjectHealth,
} from '../../domain/dashboard';
import { ISSUE_DEFS, objectLabel } from '../../domain/issues';
import { downloadFile } from '../../api/client';
import { useSwitchImport } from '../objects/useSwitchImport';
import PageHeader from '../../components/common/PageHeader';
import Icon from '../../components/common/Icon';
import ui from '../../components/common/ui.module.css';
import styles from './DashboardPage.module.css';

const MODELS_SHORT = 6;
const START_WITH = 8;

const fmtPercent = (n) => `${String(Math.round(n * 10) / 10).replace('.', ',')} %`;

/** «MAG 134» → префикс и номер отдельно: на плитке номер главный. */
function splitLabel(label) {
  const m = /^(\D*?)\s*(\d.*)$/.exec(label);
  return m ? { prefix: m[1], number: m[2] } : { prefix: '', number: label };
}

export default function DashboardPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const rows = useSelector(selectSwitches);
  const grouped = useSelector(selectGrouped);
  const prefixMap = useSelector(selectPrefixMap);
  const magToDivision = useSelector(selectMagToDivision);
  const isDeveloper = useSelector(selectIsDeveloper);
  const canEdit = useSelector(selectCanEdit);
  const user = useSelector(selectUser);
  const filterKey = useSelector((state) => state.ui.dashFilter);
  const statuses = useSelector((state) => state.collab.statuses);

  const [search, setSearch] = useState('');
  const [reportBusy, setReportBusy] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [allModels, setAllModels] = useState(false);

  const { state: importState, importFile } = useSwitchImport();
  const fileRef = useRef(null);
  const importBusy = importState.status === 'parsing' || importState.status === 'uploading';

  const isProcessed = useMemo(
    () => (mag, sw) => Boolean(statuses[`${mag}__${sw}`]),
    [statuses]
  );

  const total = rows.length;
  const clean = useMemo(() => rows.filter((r) => !(r._issues > 0)).length, [rows]);
  const completion = total ? (clean / total) * 100 : 100;

  const issueCounts = useMemo(
    () => getIssueCounts(rows).filter((x) => x.count > 0).sort((a, b) => b.count - a.count),
    [rows]
  );
  const problemRows = useMemo(
    () => filterProblemRows(rows, filterKey, search),
    [rows, filterKey, search]
  );
  const progress = useMemo(() => getProgressStats(rows, grouped, isProcessed), [rows, grouped, isProcessed]);
  const health = useMemo(
    () => getObjectHealth(grouped, isProcessed, magToDivision),
    [grouped, isProcessed, magToDivision]
  );
  const models = useMemo(() => getModelStats(rows), [rows]);

  const activeFilter = ISSUE_DEFS.find((d) => d.key === filterKey);
  const scope = user?.scopeDivisions?.length ? `Зона ответственности: ${user.scopeDivisions.join(', ')}` : 'Вся сеть';
  const objectCount = Object.keys(grouped).length;

  async function handleReport() {
    setReportBusy(true);
    setReportError(null);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await downloadFile('/api/reports/management.pdf', `switch-inspector-report-${date}.pdf`);
    } catch (error) {
      setReportError(error.message);
    } finally {
      setReportBusy(false);
    }
  }

  const openSwitch = (row) => navigate(`/objects/${row['Mag']}/${row['Коммутатор']}`);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Дашборд"
        subtitle={scope}
        actions={(
          <>
            {canEdit && (
              <>
                <button className={ui.button} onClick={() => fileRef.current?.click()} disabled={importBusy}>
                  <Icon name="upload" size={16} />
                  {importBusy ? 'Загружаю…' : 'Загрузить из Excel'}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) importFile(file);
                    e.target.value = '';
                  }}
                />
              </>
            )}
            {isDeveloper && (
              <button className={ui.button} onClick={handleReport} disabled={reportBusy}>
                <Icon name="document" size={16} />
                {reportBusy ? 'Формирую…' : 'Отчёт для руководства'}
              </button>
            )}
          </>
        )}
      />

      {importState.message && (
        <div className={`${styles.notice} ${importState.status === 'error' ? styles.noticeError : styles.noticeOk}`}>
          {importState.message}
        </div>
      )}
      {reportError && <div className={`${styles.notice} ${styles.noticeError}`}>{reportError}</div>}

      {/* ── Состояние сети ── */}
      <section className={styles.health} aria-labelledby="health-title">
        <div className={styles.summary}>
          <h2 id="health-title" className={styles.healthTitle}>Состояние сети</h2>
          <div className={styles.bigValue}>{fmtPercent(completion)}</div>
          <p className={styles.bigLabel}>
            коммутаторов без замечаний — {clean} из {total} на {objectCount} объектах
          </p>

          <div className={styles.checked}>
            <div className={styles.checkedRow}>
              <span>Обработано замечаний</span>
              <span className={styles.checkedValue}>
                {progress.total ? `${progress.done} из ${progress.total}` : 'замечаний нет'}
              </span>
            </div>
            <div className={styles.checkedBar}>
              <span style={{ width: `${progress.percent}%` }} />
            </div>
          </div>

          <ul className={styles.legend}>
            <li><i className={styles.dotClean} />без замечаний</li>
            <li><i className={styles.dotChecked} />замечание обработано</li>
            <li><i className={styles.dotOpen} />требует внимания</li>
          </ul>
        </div>

        <div className={styles.map}>
          {health.map((division) => (
            <div className={styles.division} key={division.name}>
              <div className={styles.divisionName}>{division.name}</div>
              <div className={styles.tiles}>
                {division.objects.map((o) => {
                  const label = objectLabel(o.mag, prefixMap);
                  const { prefix, number } = splitLabel(label);
                  return (
                    <button
                      key={o.mag}
                      className={styles.tile}
                      onClick={() => navigate(`/objects/${o.mag}`)}
                      title={`${label}: ${o.total} комм. — без замечаний ${o.clean}, обработано ${o.checked}, требуют внимания ${o.open}`}
                    >
                      <span className={styles.tileLabel}>
                        {prefix && <span className={styles.tilePrefix}>{prefix}</span>}
                        {number}
                      </span>
                      <span className={styles.strip} aria-hidden="true">
                        {o.clean > 0 && <span className={styles.segClean} style={{ flexGrow: o.clean }} />}
                        {o.checked > 0 && <span className={styles.segChecked} style={{ flexGrow: o.checked }} />}
                        {o.open > 0 && <span className={styles.segOpen} style={{ flexGrow: o.open }} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className={styles.columns}>
        {/* ── Проблемные записи ── */}
        <section className={styles.records} aria-labelledby="records-title">
          <div className={styles.blockHeader}>
            <h2 id="records-title" className={styles.blockTitle}>
              Проблемные записи <span className={ui.count}>{problemRows.length}</span>
            </h2>
            {activeFilter && (
              <button
                className={styles.filterChip}
                onClick={() => dispatch(setDashFilter(activeFilter.key))}
                title="Снять отбор"
              >
                {activeFilter.label}
                <span aria-hidden="true">×</span>
              </button>
            )}
            <input
              className={`${ui.search} ${styles.search}`}
              type="search"
              placeholder="Найти в записях"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Найти в проблемных записях"
            />
          </div>

          <div className={styles.tableWrap}>
            {problemRows.length === 0 ? (
              <div className={styles.empty}>
                {search || activeFilter ? 'По этому отбору записей нет.' : 'Записей с замечаниями нет.'}
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Объект</th>
                    <th>Коммутатор</th>
                    <th>IP-адрес</th>
                    <th>Шкаф</th>
                    <th>Расположение</th>
                    <th>Замечания</th>
                  </tr>
                </thead>
                <tbody>
                  {problemRows.map((row) => {
                    const done = isProcessed(row['Mag'], row['Коммутатор']);
                    return (
                      <tr
                        key={`${row['Mag']}__${row['Коммутатор']}`}
                        onClick={() => openSwitch(row)}
                        onKeyDown={(e) => { if (e.key === 'Enter') openSwitch(row); }}
                        tabIndex={0}
                        className={done ? styles.rowDone : undefined}
                      >
                        <td className={styles.nowrap}>{objectLabel(row['Mag'], prefixMap)}</td>
                        <td className={styles.mono}>{row['Коммутатор']}</td>
                        <td className={styles.mono}>{row['IP Коммутатора'] || <span className={styles.dash}>—</span>}</td>
                        <td>{row['Шкаф'] || <span className={styles.dash}>—</span>}</td>
                        <td>{row['Расположение'] || <span className={styles.dash}>—</span>}</td>
                        <td>
                          <span className={styles.issues}>
                            {String(row['Замечания'] || '').split(/;\s*/).filter(Boolean).map((text) => (
                              <span key={text} className={styles.issue}>{text}</span>
                            ))}
                            {done && <span className={styles.doneMark}>обработано</span>}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <aside className={styles.aside}>
          {/* ── Замечания по категориям — фильтр таблицы ── */}
          <section className={styles.card} aria-labelledby="issues-title">
            <h2 id="issues-title" className={styles.blockTitle}>Замечания</h2>
            <p className={styles.cardNote}>Нажмите на строку, чтобы отобрать записи.</p>
            {issueCounts.length === 0 ? (
              <p className={styles.allClear}>Замечаний нет.</p>
            ) : (
              <ul className={styles.issueList}>
                {issueCounts.map((item) => (
                  <li key={item.key}>
                    <button
                      className={`${styles.issueRow} ${filterKey === item.key ? styles.issueRowActive : ''}`}
                      onClick={() => dispatch(setDashFilter(item.key))}
                      aria-pressed={filterKey === item.key}
                    >
                      <span className={styles.issueName}>{item.label}</span>
                      <span className={styles.issueCount}>{item.count}</span>
                      <span className={styles.issueBar}>
                        <span
                          className={item.color === 'err' ? styles.barErr : styles.barWarn}
                          style={{ width: `${Math.max(item.percent, 1.5)}%` }}
                        />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* ── С чего начать: больше всего необработанных ── */}
          {progress.objects.some((o) => o.remaining > 0) && (
            <section className={styles.card} aria-labelledby="start-title">
              <h2 id="start-title" className={styles.blockTitle}>С чего начать</h2>
              <p className={styles.cardNote}>Объекты, где больше всего необработанных записей.</p>
              <ul className={styles.startList}>
                {progress.objects.filter((o) => o.remaining > 0).slice(0, START_WITH).map((o) => (
                  <li key={o.mag}>
                    <button className={styles.startRow} onClick={() => navigate(`/objects/${o.mag}`)}>
                      <span className={styles.startName}>{objectLabel(o.mag, prefixMap)}</span>
                      <span className={styles.startLeft}>осталось {o.remaining} из {o.total}</span>
                      <span className={styles.startBar}>
                        <span style={{ width: `${o.total ? (o.done / o.total) * 100 : 0}%` }} />
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* ── Модели оборудования ── */}
          <section className={styles.card} aria-labelledby="models-title">
            <h2 id="models-title" className={styles.blockTitle}>
              Модели оборудования <span className={ui.count}>{models.length}</span>
            </h2>
            <table className={styles.models}>
              <thead>
                <tr><th>Модель</th><th>Шт.</th><th>Объектов</th></tr>
              </thead>
              <tbody>
                {(allModels ? models : models.slice(0, MODELS_SHORT)).map((m) => (
                  <tr key={m.model}>
                    <td>
                      {m.model}
                      <span className={styles.shareBar}><span style={{ width: `${m.share}%` }} /></span>
                    </td>
                    <td className={styles.num}>{m.count}</td>
                    <td className={styles.num}>{m.objectCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {models.length > MODELS_SHORT && (
              <button className={styles.more} onClick={() => setAllModels((v) => !v)}>
                {allModels ? 'Свернуть' : `Показать все ${models.length}`}
              </button>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}
